import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { updateDateRangeEmissions, updateDateRangeConsumption, updateDateRangeFinancials } from "api";
import { COLORS, Loading } from "components";
import { chartColors, commonStyles } from "../const";
import { DateRangeChartProps } from "../types";

export const SiteTypeChart = ({
  chartTitle,
  dateRangeType,
  subTabName,
  startDate,
  endDate,
  searchFilters,
  currentMainTab
}: DateRangeChartProps) => {
  const [dateRangeData, setDateRangeData] = useState<any>([]);
  const apiFn = currentMainTab === "GHG Emissions" ? updateDateRangeEmissions : currentMainTab === "Consumption" ? updateDateRangeConsumption : updateDateRangeFinancials;
  const { mutateAsync: updateDateRange, isLoading } = useMutation(apiFn);
  const noDataAvailable = !isLoading && Object.values(dateRangeData).every(value =>
    typeof value === "object" && value !== null && Object.keys(value).length === 0
  );
  const { facilityType } = dateRangeData;

  const isEmissionsChart = subTabName === "location" || subTabName === "market";
  const isConsumption = currentMainTab === "Consumption";

  const data: any[] = facilityType ? Object.values(facilityType).flat() : [];
  const facilityTypeCategoriesXAxis = Array.from(new Set(data.map((entry: any) =>
    dateRangeType === "MONTHLY" && entry.year !== null
      ? entry.month + entry.year.replace("20", " '")
      : dateRangeType === "QUARTERLY"
        ? (`Q${entry.fiscalYearQuarter}`).concat(entry.fiscalYear.replace("20", " '")).concat(" FY")
        : entry.fiscalYear.concat(" FY")
  )));

  const emissionType = isEmissionsChart ? `${subTabName}BasedEmission` : isConsumption ? "consumption" : "amount";

  const facilityTypes: any = data.reduce((acc: any, entry: any) => {
    if (!acc[entry.facilityType]) {
      acc[entry.facilityType] = [];
    }
    acc[entry.facilityType].push({
      ...entry,
      [emissionType]: entry[emissionType] ?? 0,
    });
    return acc;
  }, {});

  const facilityTypesData: any = Object.keys(facilityTypes).reduce((acc: any, facilityTypeKey: string) => {
    acc[facilityTypeKey] = facilityTypeCategoriesXAxis.map((entry: string) => facilityTypes[facilityTypeKey].find((item: any) => {
      if (dateRangeType === "MONTHLY") {
        return item.month === entry.substring(0, 3);
      }
      if (dateRangeType === "QUARTERLY") {
        return item.fiscalYearQuarter === entry.substring(1, 2) && item.fiscalYear.substring(2) === entry.substring(4, 6);
      }
      return item.fiscalYear === entry.substring(0, 4);
    })?.[emissionType] ?? 0);
    return acc;
  }, {});

  const facilityTypesTotalData: any = Object.values(facilityTypesData).reduce((acc: any, facilityTypeData: any) => {
    facilityTypeData.forEach((value: number, index: number) => {
      if (!acc[index]) {
        acc[index] = 0;
      }
      acc[index] += value;
    });
    return acc;
  }, []);

  const unitYAxis = isEmissionsChart ? "tCO2e" : data[0]?.unit ?? data[0]?.currency ?? "No Data Available";

  const chartOptions = {
    chart: {
      type: "column",
      spacing: [30, 50, 20, 40],
      width: null,
      height: 450,
    },
    title: {
      text: `${chartTitle}${noDataAvailable ? ": No Data Available" : ''}`,
      align: "left",
      margin: 40,
      style: {
        ...commonStyles,
        color: COLORS.Mainblue,
        fontSize: "20px",
      },
    },
    xAxis: {
      categories: facilityTypeCategoriesXAxis,
      labels: {
        style: commonStyles,
      },
      startOnTick: false,
      endOnTick: false,
    },
    yAxis: {
      title: {
        text: unitYAxis,
        style: commonStyles,
        offset: 50
      },
      labels: {
        style: commonStyles,
      },
    },
    series: [
      {
        name: "Total",
        type: "line",
        data: facilityTypesTotalData,
        color: "transparent",
        dataLabels: {
          enabled: true,
          verticalAlign: "bottom",
          style: {
            ...commonStyles,
            textOutline: "none",
          },
        },
      },
      ...Object.keys(facilityTypesData).map((facilityTypeKey, index) => ({
        name: facilityTypeKey,
        data: facilityTypesData[facilityTypeKey],
        color: chartColors[index % chartColors.length],
        fillOpacity: 0.3,
        lineWidth: 6,
        marker: { enabled: false },
      })),
    ],
    plotOptions: {
      line: {
        stacking: "normal",
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            textOutline: "",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
          },
        },
      },
      column: {
        stacking: "normal",
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            textOutline: "",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
          },
        },
      },
    },
    legend: {
      title: {
        style: commonStyles,
      },
      align: "left",
      verticalAlign: "bottom",
      x: 20,
      margin: 30,
      maxHeight: 100,
    },
    tooltip: {
      shared: true,
      borderRadius: 8,
      shadow: false,
      padding: 24,
      useHTML: true,
      outside: true,
      positioner(labelWidth: any, labelHeight: any, point: any) {
        return {
          x: point.plotX,
          y: point.plotY - labelHeight,
        };
      },
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return `
                <div style="font-size: 12px; padding: 8px; display: flex; flex-direction: column; gap: 5px;">
                  <strong>${this!.x}</strong>
                  ${this!.points
            ?.map(
              (point) => `
                    <div style="display: flex; align-items: center; justify-content: space-between; width: 180px;">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        <span style="width: 10px; height: 10px; background: ${point.series.color}; border-radius: 50%; display: inline-block;"></span>
                        <span>${point.series.name}:</span>
                      </div>
                      <span style="font-weight: bold;">${Number(point.y!.toFixed(2)).toLocaleString('en-US')} ${unitYAxis}</span>
                    </div>
                  `
            ).join("")}
                </div>
              `;
      },
    },
    credits: {
      enabled: false,
    },
  };

  useEffect(() => {
    const fetchDateRange = async () => {
      setDateRangeData({});
      await updateDateRange({
        dateRangeRequest: {
          ...searchFilters,
          compareFromDate: startDate.toISOString().substring(0, 10),
          compareToDate: endDate.toISOString().substring(0, 10),
          groupBy: dateRangeType
        },
        chartType: 'site-type'
      }, {
        onSuccess: (response: any) => {
          setDateRangeData(response.data);
        },
        onError: () => {
          // implement error handling
        },
      })
    };

    fetchDateRange();
  }, [searchFilters, startDate, endDate, dateRangeType, updateDateRange]);

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 3,
      fontFamily: "Inter",
      "& > div": {
        borderRadius: 2,
      },
    }}>
      {isLoading && <Box sx={{ height: 300, position: "absolute", zIndex: 99999, right: "50%" }}>
        <Loading />
      </Box>}
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
      />
    </Box>
  );
};
